import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Editor } from '@tiptap/react';
import { dispatchTextBlockData, useTextBlockData } from 'adapter/in/reports/contexts/TextBlockContext';
import { Button } from '@dexma/ui-components';
import i18n from 'asset/translation';
import useTextBlock from 'adapter/in/common/widget/TextBlock/useTextBlock';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { UpdateTextBlockCriteriaBuilder } from 'use-case/widget/update/updateTextBlockCriteria';
import { dispatchReportLayoutsData } from 'adapter/in/reports/contexts/ReportLayoutDataContext';
import { ReadLayoutsCriteriaBuilder } from 'use-case/layout/read/readLayoutsCriteria';
import { isSome } from 'fp-ts/lib/Option';
import { isDashboardFrame } from 'adapter/in/common/widget/widgetBuilder';
import { StyledEditorContent, StyledTiptapWrapper } from 'adapter/in/common/components/Text/style';

type TiptapProps = {
    editor: Editor | null;
    textBlockId: string;
    content: string;
    dashboardId?: string;
};

const EditorContentWrapper = styled.div<{ $isDashboardFrame: boolean }>`
    flex: 1;
    min-height: 0;
    display: flex;
    padding: ${({ $isDashboardFrame }) => ($isDashboardFrame ? '0px 8px' : '8px')};

    .ProseMirror {
        height: 100%;
        outline: none;
    }
`;

const ActionsFooterWrapper = styled.div`
    position: absolute;
    bottom: 0;
    right: 0;
`;

const StyledButton = styled(Button)`
    margin-left: 5px;
`;

const hasBorderNoneDeclaration = (style: string | null): boolean => /(?:^|;)\s*border\s*:\s*none\s*(?:;|$)/i.test(style ?? '');

const hasBorderlessTable = (html: string): boolean => {
    if (typeof DOMParser === 'undefined') return false;

    const documentFromHtml = new DOMParser().parseFromString(html, 'text/html');
    const tableElements = Array.from(documentFromHtml.querySelectorAll('table, th, td'));

    return tableElements.some((element) => hasBorderNoneDeclaration(element.getAttribute('style')));
};

const Tiptap: React.FC<TiptapProps> = ({ editor, textBlockId, content, dashboardId }) => {
    const { editorBeingEdited } = useTextBlockData();

    const { setEditorBeingEdited } = dispatchTextBlockData();
    const { updateTextBlock } = useTextBlock();
    const { execute: update, isLoading, data } = useCase(updateTextBlock, false);
    const { getLayouts } = dispatchReportLayoutsData();
    const [isUpdating, setIsUpdating] = useState(false);

    const confirmChanges = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (editor && textBlockId) {
            const htmlContent = editor.getHTML();
            update(UpdateTextBlockCriteriaBuilder.create().withRichText(htmlContent).withWidgetId(textBlockId).build());
            setIsUpdating(true);
        }
    };

    useEffect(() => {
        if (isSome(data)) {
            setIsUpdating(false);
            setEditorBeingEdited(null);
        }
    }, [data]);

    useEffect(() => {
        if (!isLoading && isUpdating && dashboardId) {
            getLayouts(ReadLayoutsCriteriaBuilder.create().withDashboardId(dashboardId).build());
        }
    }, [isLoading, isUpdating, dashboardId]);

    const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (editor && content) {
            editor.commands.setContent(content);
        }
        setEditorBeingEdited(null);
    };

    const dashboardFrame = isDashboardFrame(textBlockId);
    const displayTableBorders = !hasBorderlessTable(content);

    return (
        editor && (
            <StyledTiptapWrapper data-testid='tiptap-editor' $displayTableBorders={displayTableBorders}>
                <EditorContentWrapper $isDashboardFrame={dashboardFrame}>
                    <StyledEditorContent
                        data-testid='editor-content'
                        $isBeingEdited={editorBeingEdited?.textBlockId === textBlockId}
                        editor={editor}
                    />
                </EditorContentWrapper>
                {editorBeingEdited?.textBlockId === textBlockId && (
                    <ActionsFooterWrapper>
                        <StyledButton
                            variant='primary'
                            onClick={(e) => confirmChanges(e)}
                            data-testid='tiptap-confirm-changes-button'
                            size='small'
                            text={i18n.t('common.save')}
                            disabled={isLoading && isUpdating}
                        />
                        <StyledButton
                            variant='outline'
                            onClick={handleCancel}
                            data-testid='tiptap-cancel-changes-button'
                            size='small'
                            text={i18n.t('common.cancel')}
                        />
                    </ActionsFooterWrapper>
                )}
            </StyledTiptapWrapper>
        )
    );
};

export default Tiptap;
